<template>
  <div v-if="isLoadingAuth || isLoading" class="flex items-center justify-center h-screen bg-gray-950">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>

  <div v-else-if="!user" class="text-red-300 text-center p-6 bg-gray-800 min-h-screen flex items-center justify-center">
    <div class="flex items-center gap-3">
      <i class="fas fa-exclamation-triangle text-2xl"></i>
      <span class="text-xl font-semibold text-indigo-200">กรุณาเข้าสู่ระบบ</span>
    </div>
  </div>

  <div v-else class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 text-white rounded-lg p-5 flex flex-col gap-4 shadow-xl animate-scaleIn">
      <!-- Error Display -->
      <div v-if="error" class="p-2 bg-red-900 text-red-300 text-center text-sm rounded-md">
        {{ error }}
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="p-2 bg-green-900 text-green-300 text-center text-sm rounded-md">
        {{ successMessage }}
      </div>

      <!-- Header -->
      <div class="flex justify-between items-center border-b border-gray-700 pb-3">
        <div class="flex items-center gap-3">
          <i class="fas fa-suitcase text-indigo-300 text-2xl"></i>
          <div>
            <h1 class="text-lg font-semibold text-indigo-300">ย้ายไอเทมไปกระเป๋า</h1>
            <p class="text-indigo-300 text-xs">เลือกไอเทมที่ต้องการย้าย</p>
            <p class="text-indigo-300 text-xs">น้ำหนักรวมที่เลือก: <strong>{{ selectedWeight }}</strong></p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <select
            v-model="sortPreference"
            class="bg-gray-700 text-indigo-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 border border-gray-600"
          >
            <option value="type">เรียงตามประเภท</option>
            <option value="rarity">เรียงตาม Rarity</option>
            <option value="inventory">เรียงตามรายการล่าสุด</option>
          </select>
        </div>
        <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 group" aria-label="ปิด">
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Item List -->
      <div class="max-h-96 overflow-y-auto p-3 scrollbar-thin">
        <div v-if="isLoading" class="col-span-full text-center text-indigo-200 py-4 text-sm">กำลังโหลดไอเทม...</div>
        <div v-else-if="sortedItems.length === 0" class="col-span-full text-center text-indigo-200 py-4 text-sm">
          ไม่พบไอเทมในคลัง
        </div>
        <transition-group v-else name="item" tag="div" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div
            v-for="item in sortedItems"
            :key="item.instanceId"
            @click="toggleItemSelection(item)"
            :class="[
              'cursor-pointer rounded-md border transition-all duration-200 relative p-2',
              selectedItems.includes(item.instanceId) ? 'bg-indigo-700 border-indigo-400' : 'bg-gray-700 border-gray-600 hover:bg-gray-600 hover:border-indigo-400'
            ]"
            role="button"
            :aria-label="`เลือกไอเทม ${item.name}`"
          >
            <div class="flex items-center gap-2">
              <img
                :src="item.img || 'default-item.png'"
                :alt="item.name"
                class="w-8 h-8 rounded-md object-cover border-2"
                :class="[
                  item.rarityColors?.borderColor || 'border-indigo-400',
                  item.rarityColors?.bgColor || 'bg-gray-700'
                ]"
                @error="handleImageError"
              />
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-indigo-300">
                  {{ item.type === 'หนังสือ' ? `${bookTypeOrder[item.name] || ''}. ` : '' }}{{ item.name }}
                  <span v-if="item.level">+{{ item.level }}</span>
                  <span v-if="item.quantity > 1">x{{ item.quantity }}</span>
                </h3>
                <p class="text-xs text-indigo-200">ประเภท: {{ item.type }}</p>
                <p class="text-xs text-indigo-200">น้ำหนัก: {{ (item.weight || 0) * (item.quantity || 1) }}</p>
              </div>
              <input
                type="checkbox"
                :checked="selectedItems.includes(item.instanceId)"
                class="absolute top-2 right-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded"
                @click.stop="toggleItemSelection(item)"
                :aria-label="`เลือก ${item.name}`"
              />
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Back to Top Button -->
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-20 right-6 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-md transition-colors duration-200"
        aria-label="กลับไปด้านบน"
      >
        <i class="fas fa-arrow-up text-sm"></i>
      </button>

      <!-- Action Buttons -->
      <div class="flex justify-between items-center gap-3 border-t border-gray-700 pt-3">
        <div class="flex gap-3">
          <button
            v-if="sortedItems.length > 0"
            @click="selectAllItems"
            :class="[
              'bg-blue-600 hover:bg-blue-500 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700',
              { 'opacity-60 cursor-not-allowed': isMoving || sortedItems.length === 0 }
            ]"
            :disabled="isMoving || sortedItems.length === 0"
          >
            <i class="fas fa-check-square text-xs"></i> เลือกทั้งหมด
          </button>
          <button
            v-if="selectedItems.length > 0"
            @click="deselectAllItems"
            :class="[
              'bg-gray-600 hover:bg-gray-500 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-gray-600',
              { 'opacity-60 cursor-not-allowed': isMoving || selectedItems.length === 0 }
            ]"
            :disabled="isMoving || selectedItems.length === 0"
          >
            <i class="fas fa-times-circle text-xs"></i> ยกเลิกเลือกทั้งหมด
          </button>
        </div>
        <div class="flex gap-3">
          <button
            v-if="selectedItems.length > 0"
            @click="confirmMove = true"
            :class="[
              'bg-blue-600 hover:bg-blue-500 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700',
              { 'opacity-60 cursor-not-allowed': isMoving || selectedItems.length === 0 }
            ]"
            :disabled="isMoving || selectedItems.length === 0"
          >
            <span v-if="!isMoving"><i class="fas fa-suitcase text-xs"></i> ย้ายไปกระเป๋า ({{ selectedItems.length }})</span>
            <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <button
            @click="closeModal"
            class="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
          >
            <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
          </button>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <transition name="fade">
        <div
          v-if="confirmMove"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg"
        >
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md animate-scaleIn">
            <div class="flex items-center gap-2 mb-4">
              <i class="fas fa-suitcase text-white text-lg"></i>
              <h3 class="text-lg font-semibold text-white">ยืนยันการย้ายไอเทม</h3>
            </div>
            <p class="text-sm text-gray-400 mb-4">คุณแน่ใจหรือไม่ว่าต้องการย้าย {{ selectedItems.length }} ไอเทม (น้ำหนักรวม {{ selectedWeight }}) ไปกระเป๋า?</p>
            <div v-if="error" class="text-red-300 mb-4 text-sm">{{ error }}</div>
            <div class="flex justify-end space-x-2">
              <button
                @click="moveItemsToBag"
                class="py-2 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-md transition-all duration-200 flex items-center gap-2"
                :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isMoving }"
                :disabled="isMoving"
              >
                <span v-if="!isMoving"><i class="fas fa-check text-sm"></i> ยืนยัน</span>
                <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
              </button>
              <button
                @click="confirmMove = false"
                class="py-2 px-4 bg-gray-600 hover:bg-gray-500 border border-gray-600 text-white rounded-md transition-all duration-200 flex items-center gap-2"
              >
                <i class="fas fa-times text-sm"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Loading Overlay -->
      <transition name="fade">
        <div
          v-if="isMoving"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg"
        >
          <div class="animate-spin-pulse rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import getUser from '@/composables/getUser';
import { projectFunctions } from '@/firebase/config';
import { ref, computed, onMounted } from 'vue';
import { books } from '@/constants/books';

export default {
  name: 'MoveToBagModal',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  emits: ['close', 'items-moved'],
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const isLoading = ref(false);
    const error = ref(null);
    const successMessage = ref(null);
    const selectedItems = ref([]);
    const confirmMove = ref(false);
    const isMoving = ref(false);
    const showBackToTop = ref(false);
    const sortPreference = ref('type');

    // Compute total weight of selected items (support quantity)
    const selectedWeight = computed(() => {
      return selectedItems.value.reduce((sum, instanceId) => {
        const item = props.items.find(i => i.instanceId === instanceId);
        return sum + (item ? (item.weight || 0) * (item.quantity || 1) : 0);
      }, 0);
    });

    const defaultSortOrder = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'อวัยวะจักรกล','หนังสือ', 'อาหาร', 'แผนที่', 'รักษา', 'กัปดัก', 'วัสดุ', 'ทั่วไป'
    ];
    const sortOrder = ref([...defaultSortOrder]);

    const bookTypeOrder = {};
    const typeCounts = {};
    books.forEach(book => {
      if (!typeCounts[book.type]) typeCounts[book.type] = 0;
      typeCounts[book.type]++;
      bookTypeOrder[book.name] = typeCounts[book.type];
    });

    const sortedItems = computed(() => {
      let itemsList = [...props.items];
      if (sortPreference.value === 'type') {
        const typeOrder = sortOrder.value.reduce((acc, type, index) => {
          acc[type] = index + 1;
          return acc;
        }, {});
        itemsList.sort((a, b) => {
          const orderA = typeOrder[a.type] || 999;
          const orderB = typeOrder[b.type] || 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const rarityA = a.rarity ? String(a.rarity) : '';
          const rarityB = b.rarity ? String(b.rarity) : '';
          if (rarityA !== rarityB) {
            return rarityA.localeCompare(rarityB, 'th');
          }
          return a.name.localeCompare(b.name, 'th');
        });
      } else if (sortPreference.value === 'rarity') {
        itemsList.sort((a, b) => {
          const rarityA = a.rarity ? String(a.rarity) : '';
          const rarityB = b.rarity ? String(b.rarity) : '';
          if (rarityA !== rarityB) {
            return rarityA.localeCompare(rarityB, 'th');
          }
          return a.name.localeCompare(b.name, 'th');
        });
      } else if (sortPreference.value === 'inventory') {
        // No sort, keep original order
        return itemsList;
      }
      return itemsList;
    });

    // Handle image error
    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    // Toggle item selection
    const toggleItemSelection = (item) => {
      if (!item?.instanceId) return;
      const index = selectedItems.value.indexOf(item.instanceId);
      if (index === -1) {
        selectedItems.value.push(item.instanceId);
      } else {
        selectedItems.value.splice(index, 1);
      }
      error.value = '';
      successMessage.value = '';
      console.log('Selected items:', selectedItems.value);
    };

    // Select all items
    const selectAllItems = () => {
      selectedItems.value = props.items.map(item => item.instanceId);
      error.value = '';
      successMessage.value = '';
      console.log('All items selected:', selectedItems.value);
    };

    // Deselect all items
    const deselectAllItems = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
      console.log('All items deselected');
    };

    // Move selected items to bag
    const moveItemsToBag = async () => {
      if (!user.value || selectedItems.value.length === 0) {
        error.value = 'กรุณาเลือกไอเทมเพื่อย้าย';
        return;
      }
      isMoving.value = true;
      error.value = '';
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({ action: 'moveItemsToBag', instanceIds: selectedItems.value });
        console.log('Move to bag response:', response);
        const movedInstanceIds = [...selectedItems.value]; // Copy for emit
        successMessage.value = `ย้าย ${selectedItems.value.length} ไอเทมไปกระเป๋าสำเร็จ!`;
        selectedItems.value = [];
        confirmMove.value = false;
        emit('items-moved', movedInstanceIds); // Emit movedInstanceIds for parent to update locally
        setTimeout(() => {
          emit('close');
          successMessage.value = '';
        }, 1000); // Delay closing to show success message
      } catch (err) {
        console.error('Error moving items to bag:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการย้ายไอเทม';
      } finally {
        isMoving.value = false;
      }
    };

    // Close modal
    const closeModal = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
      confirmMove.value = false;
      emit('close');
    };

    // Scroll to top
    const scrollToTop = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Monitor scroll for back-to-top button
    const handleScroll = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        showBackToTop.value = container.scrollTop > 100;
      }
    };

    onMounted(() => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }
    });

    return {
      user,
      isLoadingAuth,
      isLoading,
      error,
      successMessage,
      selectedItems,
      confirmMove,
      isMoving,
      showBackToTop,
      selectedWeight,
      sortedItems,
      sortPreference,
      handleImageError,
      toggleItemSelection,
      selectAllItems,
      deselectAllItems,
      moveItemsToBag,
      closeModal,
      scrollToTop,
      bookTypeOrder
    };
  },
};
</script>


<style scoped>
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.item-enter-active,
.item-leave-active {
  transition: all 0.3s ease;
}

.item-enter-from,
.item-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #6366f1;
}
</style>